import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import {
  DefaultReferenceNumber,
  GetCustomerDetailsQuery,
  NewShipmentInput,
  ShipmentCommodityType,
  ShipmentConstraintType,
  ShipmentLocationInput,
  ShipmentLocationType,
  ShipmentReferenceNumberType,
  Status,
  TrailerType,
  useGenerateShipmentRouteMutation,
  useGetBrokersQuery,
  useGetCustomersQuery,
  useGetOrganizationSettingsQuery,
  useGetReceiversQuery,
  useGetShippersQuery,
} from "../../../graphql/generated";
import { resolveDefaultLiquidTrailerTypes } from "../../../utils/liquid/resolveDefaultLiquidTrailerTypes";

import {
  FieldErrors,
  FormError,
  NewShipmentInputData,
} from "../../../redux/slices/Types";
import {
  editShipment,
  setCommodityType,
  setIsCommodityDriven,
} from "../../../redux/slices/shipment/ShipmentForm.slice";
import { flatten, groupBy, isEmpty, keyBy, mapValues, omit, sum } from "lodash";
import { addMinutes, addYears, isAfter } from "date-fns";
import _ from "lodash";
import { ShipmentDetailsProps } from "../ShipmentDetails/ShipmentDetails";
import { formatDateTimeForLocation } from "../../../utils/labels/formatDateTime";
import LocationDrivenShipmentForm from "./LocationDrivenShipmentForm";
import useConfirmBeforeLeave from "../../../utils/hooks/useConfirmBeforeLeave";
import CommodityDrivenShipmentForm from "./CommodityDrivenShipmentForm";
import { Feature, useFeature } from "../../account/Access/FeatureGuard";
import useDialog from "../../../utils/hooks/useDialog";
import {
  referenceNumberTypeToShipmentField,
  ShipmentReferenceNumberField,
} from "./ShipmentReferencesForm/ShipmentReferencesForm";
import { Box, FormControlLabel, Stack } from "@mui/material";
import IconSwitch from "../../common/IconSwitch";
import { useConfirm } from "material-ui-confirm";
import { useTranslation } from "react-i18next";
import { testIds } from "./ShipmentForm.testIds";

export type ShipmentFormProps = {
  onSubmit: (shipment: NewShipmentInput) => void;
  loading: boolean;
  editMode?: boolean;
  isChildLoad?: boolean;
  isSplit?: boolean;
  isFromSplit?: boolean;
  onFetchCustomerDetails?: (
    customer: string
  ) => Promise<GetCustomerDetailsQuery["customerById"]>;
};

export default function ShipmentForm({
  onSubmit,
  loading = false,
  editMode = false,
  isChildLoad,
  isSplit,
  isFromSplit,
  onFetchCustomerDetails,
}: ShipmentFormProps) {
  const { t } = useTranslation(["orders", "common"]);
  const [validating, setValidating] = useState(false);

  const dispatch = useDispatch();
  const shipmentInput = useSelector(
    (state: RootState) => state.shipmentReducer.shipment
  );
  const commodityType = useSelector(
    (state: RootState) => state.shipmentReducer.shipment.commodityType
  );
  const isCommodityDriven = useSelector(
    (state: RootState) => state.shipmentReducer.isCommodityDriven
  );

  const { cancelConfirm } = useConfirmBeforeLeave(shipmentInput);
  const confirm = useConfirm();

  const shipmentLocations = useSelector((state: RootState) => {
    return state.shipmentReducer.shipment.shipmentLocations;
  });

  const brokersQuery = useGetBrokersQuery();
  const customersQuery = useGetCustomersQuery();
  const shippersQuery = useGetShippersQuery();
  const receiversQuery = useGetReceiversQuery();
  const generateShipmentRouteMutation = useGenerateShipmentRouteMutation();
  const { showDialog } = useDialog();

  const [shipmentLocationErrors, setShipmentLocationErrors] =
    useState<Array<FormError>>();

  const [errors, setErrors] = useState<FieldErrors>();

  const findReceiver = (id: string) => {
    return receiversQuery.data?.receivers.data.find((s) => s._id === id);
  };

  const findShipper = (id: string) => {
    return shippersQuery.data?.shippers.data.find((s) => s._id === id);
  };

  const validateShipmentLocationForms = (): Array<FormError> => {
    const errors: Array<FormError> = [];

    const allReceivedGoods = flatten(
      shipmentLocations.map((location) => location.receivedGoods)
    );
    const allShippedGoodsById = keyBy(
      flatten(
        shipmentInput.shipmentLocations.map(
          (location) => location.shippedGoods || []
        )
      ),
      "_id"
    );
    const receivedQtyByGoodId = mapValues(
      groupBy(allReceivedGoods, (receivedGood) => receivedGood.goodId),
      (group) =>
        sum(
          group.map(
            (receivedGood) =>
              receivedGood.quantity ||
              allShippedGoodsById[receivedGood.goodId].quantity
          )
        )
    );

    for (let i = 0; i < shipmentLocations.length; i++) {
      const err: FieldErrors = {};
      if (shipmentLocations[i].locationType === ShipmentLocationType.Pickup) {
        if (
          !shipmentLocations[i].location ||
          (!shipmentLocations[i].location.latitude &&
            !shipmentLocations[i].location.longitude)
        ) {
          err["name"] = `Please select a shipper`;
        }
      } else {
        if (
          !shipmentLocations[i].location ||
          (!shipmentLocations[i].location.latitude &&
            !shipmentLocations[i].location.longitude)
        ) {
          err["name"] = `Please select a receiver`;
        }
      }

      // Pickups have optional dates
      if (
        shipmentLocations[i].timeWindows.length ||
        shipmentLocations[i].locationType === ShipmentLocationType.DropOff
      ) {
        const action =
          shipmentLocations[i].locationType === ShipmentLocationType.Pickup
            ? "Pickup"
            : "Delivery";

        if (!shipmentLocations[i].timeWindows.length) {
          err["date"] = `Please select a ${action} date`;
        } else if (
          shipmentLocations[i].timeWindows[0].fromDate <
          addYears(new Date(), -10)
        ) {
          err["date"] = `Date is too far back in the past`;
        }

        if (
          shipmentLocations[i].timeWindows[0]?.fromDate &&
          shipmentLocations[i].timeWindows[0]?.toDate
        ) {
          if (
            new Date(shipmentLocations[i].timeWindows[0].fromDate) >
            new Date(shipmentLocations[i].timeWindows[0].toDate)
          ) {
            err[
              "date"
            ] = `${action} time end cannot be before ${action.toLowerCase()} time start`;
          }
        }
      }

      if (
        !isCommodityDriven &&
        shipmentLocations[i].locationType === ShipmentLocationType.Pickup
      ) {
        const shippedGoods = shipmentLocations[i].shippedGoods;
        if (!shippedGoods.length) {
          err["goods"] =
            "You need to define at least one commodity for this shipper";
        }
        shippedGoods.forEach((shippedGood) => {
          const receivedQty = receivedQtyByGoodId[shippedGood._id];
          if (!receivedQty) {
            err[
              "goods"
            ] = `Commodity "${shippedGood.label}" needs to be delivered to at least one receiver`;
          } else if (shippedGood.quantity > receivedQty) {
            err["goods"] = `Commodity "${shippedGood.label}" has ${
              shippedGood.quantity - receivedQty
            } items not delivered`;
          }
        });
      }

      if (
        !isCommodityDriven &&
        shipmentLocations[i].locationType === ShipmentLocationType.DropOff
      ) {
        const receivedGoods = shipmentLocations[i].receivedGoods;
        if (!receivedGoods.length) {
          err["goods"] =
            "You need to select at least one commodity for this receiver";
        }
        receivedGoods.forEach((receivedGood) => {
          const shippedGood = allShippedGoodsById[receivedGood.goodId];
          const receivedQty = receivedQtyByGoodId[receivedGood.goodId];
          if ((shippedGood?.quantity || 0) < receivedQty) {
            err["goods"] = `Commodity "${
              shippedGood?.label || receivedGood.goodProfileId
            }" has +${
              receivedQty - (shippedGood?.quantity || 0)
            } items delivered in excess`;
          }
        });
      }

      if (!_.isEmpty(err)) {
        errors.push({ formId: shipmentLocations[i]._id, error: err });
      }
    }

    if (errors.length) {
      return errors;
    } else {
      return [];
    }
  };

  const buildShipmentLocations = (): Array<ShipmentLocationInput> => {
    const adaptedShipmentLocations: ShipmentLocationInput[] =
      shipmentInput.shipmentLocations.map((shipmentLocation) => {
        return {
          ...omit(shipmentLocation, "isDefaultFor"),
        };
      });
    return adaptedShipmentLocations;
  };

  const toShipment = (
    data: NewShipmentInputData
  ): ShipmentDetailsProps["shipment"] | null => {
    const customerOrBroker = [
      ...(brokersQuery.data?.brokers.data || []),
      ...(customersQuery.data?.customers.data || []),
    ].find((entity) => entity._id === shipmentInput.customer);

    if (!customerOrBroker) {
      return null;
    }

    const shipmentLocations = buildShipmentLocations();
    return {
      ...data,
      documents: data.documents?.map((document) => ({
        ...document,
        _id: document._id || "",
        receiver: {
          _id: document.receiver || "",
          name: findReceiver(document.receiver || "")?.name || "",
        },
        shipper: {
          _id: document.receiver || "",
          name: findShipper(document.shipper || "")?.name || "",
        },
        isBillable: document.isBillable || false,
        isForRatecons: document.isForRatecons || false,
        isForReceivers: document.isForReceivers || false,
      })),
      notes:
        data.notes?.map((note) => ({
          ...note,
          _id: note._id || "",
          receiver: {
            _id: note.receiver || "",
            name: findReceiver(note.receiver || "")?.name || "",
          },
          shipper: {
            _id: note.receiver || "",
            name: findShipper(note.shipper || "")?.name || "",
          },
        })) || [],
      customer: customerOrBroker,
      _id: "",
      shipmentNumber: shipmentInput.shipmentNumber || "",
      loadType: data.loadType,
      trailerType: data.trailerType,
      additionalTrailerTypes: data.additionalTrailerTypes,
      constraints:
        data.constraints?.map((c) => ({
          type: c.type as ShipmentConstraintType,
          unit: c.unit,
          value: c.value,
        })) || [],
      shipmentLocations: shipmentLocations.map((s) => ({
        _id: s._id || "",
        location: s.location,
        locationType: s.locationType,
        receivedGoods: s.receivedGoods,
        receiver:
          s.locationType === ShipmentLocationType.DropOff
            ? findReceiver(s.receiver || "")
            : null,
        shippedGoods: s.shippedGoods,
        shipper:
          s.locationType === ShipmentLocationType.Pickup
            ? findShipper(s.shipper || "")
            : null,
        timeWindows: s.timeWindows,
        name: s.name,
        addressLabel: s.addressLabel,
      })),
      status: Status.Unassigned,
      createdBy: "",
      charges: data.charges.map((chargeInput) => ({
        ...chargeInput,
        _id: chargeInput._id || "",
        total: chargeInput.rate * chargeInput.unit,
        billingRule: chargeInput.billingRule
          ? {
              _id: chargeInput.billingRule,
            }
          : null,
        ratingTable: chargeInput.ratingTable
          ? {
              _id: chargeInput.ratingTable,
              label: "",
            }
          : null,
      })),
      expenses: data.expenses?.map((expenseInput) => ({
        ...expenseInput,
        _id: expenseInput._id || "",
      })),
    };
  };

  const validate = async () => {
    //Shipment Location Forms
    let errors: FieldErrors = {};
    // if (!shipmentInput.customer) {
    //   errors = {
    //     ...errors,
    //     customer: "Please select a Customer / Broker",
    //   };
    // }

    if (!shipmentInput.trailerType) {
      errors["trailerType"] = "Please select a Trailer Type";
    }

    if (!isEmpty(errors)) {
      setErrors(errors);
    } else {
      setErrors(undefined);
    }
    const shipmentErrors = validateShipmentLocationForms();
    setShipmentLocationErrors(shipmentErrors);

    if (shipmentErrors.length || !isEmpty(errors)) {
      return false;
    }
    try {
      setValidating(true);
      const shipmentInputForRouteValidation: NewShipmentInput = {
        billOfLadingNumber: shipmentInput.billOfLadingNumber,
        charges: shipmentInput.charges,
        constraints: shipmentInput.constraints,
        customer: shipmentInput.customer,
        loadType: shipmentInput.loadType,
        postOfficeNumber: shipmentInput.postOfficeNumber,
        trailerType: shipmentInput.trailerType,
        shipmentLocations: buildShipmentLocations(),
      };
      const shipmentRouteResponse =
        await generateShipmentRouteMutation.mutateAsync({
          shipment: shipmentInputForRouteValidation,
        });
      const shipmentRoute = shipmentRouteResponse.generateShipmentRoute;
      // setLocalMileage(shipmentRoute.routeDistance);

      const shipmentRouteErrors: FormError[] = (shipmentRoute.locations || [])
        .map((location, index) => {
          if (!location.timeWindows.length) {
            return null;
          }
          if (
            isAfter(
              new Date(location.arrivalTime),
              new Date(location.timeWindows[0].toDate)
            )
          ) {
            const err: FieldErrors = {};
            const originalLocation = toShipment(
              shipmentInput
            )?.shipmentLocations.find((loc) => loc._id === location._id);
            err[
              "date"
            ] = `Earliest possible arrival time is ${formatDateTimeForLocation(
              addMinutes(new Date(location.arrivalTime), 1),
              originalLocation
            )}`;
            return {
              formId: location._id,
              error: err,
            };
          }
          return null;
        })
        .filter(Boolean) as FormError[];
      if (shipmentRouteErrors.length) {
        setShipmentLocationErrors(shipmentRouteErrors);
        return false;
      }
      handleUpdate({
        route: shipmentRoute,
      });
    } catch (error) {
      console.error(error);
      showDialog({
        type: "error",
        title: "Error validating order route",
        description: (error as Error).message,
      });
      return false;
    } finally {
      setValidating(false);
    }
    setShipmentLocationErrors([]);
    return true;
    // if (activeStep === 1) {
    //   const trailerRequirementsValidationResult =
    //     trailerRequirementsSchema.validate(shipment, {
    //       abortEarly: false,
    //       allowUnknown: true,
    //     });
    //   if (trailerRequirementsValidationResult.error) {
    //     console.log(trailerRequirementsValidationResult.error);
    //     return;
    //   }
    // }

    // if (activeStep === 2) {
    //   const _shipment: NewShipmentInput = {
    //     ...omit(
    //       shipment,
    //       "events",
    //       "issues",
    //       "route",
    //       "id",
    //       "isSplit",
    //       "isFromSplit",
    //       "childShipmentIds",
    //       "date",
    //       "predefinedRecurrence",
    //       "parentShipment"
    //     ),
    //     shipmentLocations: buildShipmentLocations(),
    //     documents: shipment.documents.map((document) => omit(document, "_id")),
    //     notes: shipment.notes?.map((note) => omit(note, "_id")),
    //     charges: shipment.charges.map((charge) =>
    //       omit(charge, "_id", "relatedTransactionId", "total")
    //     ),
    //     expenses: shipment.expenses?.map((charge) =>
    //       omit(charge, "_id", "total")
    //     ),
    //   };
    //   cancelConfirm();
    //   onSubmit(_shipment, uploadedDocuments);
    // }
    // if (activeStep < 2) {
    //   setActiveStep((prevActiveStep) => prevActiveStep + 1);
    // }
  };

  const handleSubmit = () => {
    const customer =
      shipmentInput.customer ||
      (shipmentInput.shipmentLocations
        .filter(
          (location) => location.locationType === ShipmentLocationType.DropOff
        )
        .map(
          (location) =>
            location.receiver &&
            findReceiver(location.receiver)?.parentBusinessEntityId
        )
        .filter(Boolean)[0] as string);
    const _shipment: NewShipmentInput = {
      ...omit(
        shipmentInput,
        "events",
        "issues",
        "route",
        "id",
        "isSplit",
        "isFromSplit",
        "childShipmentIds",
        "date",
        "predefinedRecurrence",
        "parentShipment",
        "route",
        "isApplicableChargeOrderCancellation",
        // read-only resolved field (dispatcher name derived from createdBy);
        // not part of NewShipmentInput, so it must not be sent (e.g. on duplicate).
        "dispatcher"
      ),
      customer,
      shipmentLocations: buildShipmentLocations(),
      documents: shipmentInput.documents.map((document) =>
        omit(document, "_id")
      ),
      notes: shipmentInput.notes?.map((note) => omit(note, "_id")),
      charges: shipmentInput.charges.map((charge) =>
        omit(charge, "_id", "relatedTransactionId", "total")
      ),
      expenses: shipmentInput.expenses?.map((charge) =>
        omit(charge, "_id", "total")
      ),
    };
    cancelConfirm();
    onSubmit(_shipment);
    return Promise.resolve();
  };

  const handleChange = (
    name: string,
    value: string | string[] | null | undefined
  ) => {
    let _shipment: NewShipmentInputData;
    _shipment = {
      ...shipmentInput,
      [name]: value,
    };
    setErrors(_.omit(errors, name));
    dispatch(editShipment(_shipment));
  };

  const handleUpdate = (updates: Partial<NewShipmentInputData>) => {
    const updatedShipment: NewShipmentInputData = {
      ...shipmentInput,
      ...updates,
    };
    if (updates.date) {
      updatedShipment.shipmentLocations = updatedShipment.shipmentLocations.map(
        (location) => ({
          ...location,
        })
      );
    }
    if (updates.trailerType) {
      setErrors(_.omit(errors, "trailerType"));
    }
    dispatch(editShipment(updatedShipment));
  };

  const orgSettingsQuery = useGetOrganizationSettingsQuery(
    {},
    { refetchOnWindowFocus: false }
  );
  const defaultTrailerTypesSetting =
    orgSettingsQuery.data?.organizationSettings?.dispatching
      ?.defaultTrailerTypes;
  const defaultLiquidTrailers = useMemo(
    () => resolveDefaultLiquidTrailerTypes(defaultTrailerTypesSetting),
    [defaultTrailerTypesSetting]
  );

  const switchCommodityType = useCallback(
    (type: ShipmentCommodityType) => {
      dispatch(setIsCommodityDriven(type === ShipmentCommodityType.Liquid));
      if (type === ShipmentCommodityType.Liquid) {
        dispatch(
          setCommodityType({
            commodityType: type,
            ...defaultLiquidTrailers,
          })
        );
      } else {
        dispatch(
          setCommodityType({
            commodityType: type,
            trailerType: TrailerType.Conestoga,
            additionalTrailerTypes: [],
          })
        );
      }
    },
    [dispatch, defaultLiquidTrailers]
  );

  useEffect(() => {
    async function updateShipmentWithCustomerDefaults() {
      // Only fetch customer details in create mode when customer is selected
      if (editMode || !shipmentInput.customer || !onFetchCustomerDetails) {
        return;
      }

      const customerDetails = await onFetchCustomerDetails(
        shipmentInput.customer
      );
      if (!customerDetails?.defaultReferenceNumbers) return;

      // Initialize reference number fields with empty arrays to reset reference numbers
      const defaultReferenceFields:
        | Record<ShipmentReferenceNumberField, string[]>
        | {} = Object.values(ShipmentReferenceNumberType).reduce(
        (acc, refType) => ({
          ...acc,
          [referenceNumberTypeToShipmentField[refType]]: [],
        }),
        {}
      );

      const defaultReferenceTypes = [
        ShipmentReferenceNumberType.BillOfLadingNumber,
        ShipmentReferenceNumberType.PurchaseOrderNumber,
        ShipmentReferenceNumberType.ReferenceNumber,
      ];

      const defaultDefaultReferenceNumbers: DefaultReferenceNumber[] =
        defaultReferenceTypes.map((referenceType) => ({
          referenceNumberType: referenceType,
          referenceNumberValue: "",
        }));

      const customerReferenceFields = (
        customerDetails.defaultReferenceNumbers.length
          ? customerDetails.defaultReferenceNumbers
          : defaultDefaultReferenceNumbers
      ).reduce((acc, { referenceNumberType, referenceNumberValue }) => {
        const fieldName =
          referenceNumberTypeToShipmentField[referenceNumberType];
        if (!acc[fieldName]) {
          acc[fieldName] = [];
        }
        acc[fieldName].push(referenceNumberValue);
        return acc;
      }, {} as Record<ShipmentReferenceNumberField, string[]>);

      // Merge default empty arrays with customer values
      const mergedReferenceFields = {
        ...defaultReferenceFields,
        ...customerReferenceFields,
      };

      dispatch(
        editShipment({
          ...shipmentInput,
          ...mergedReferenceFields,
        })
      );
    }

    updateShipmentWithCustomerDefaults();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentInput.customer]);

  const commodityManagementFeature = useFeature(Feature.CommodityManagement);

  useEffect(() => {
    if (!editMode && commodityManagementFeature.isAvailable) {
      switchCommodityType(ShipmentCommodityType.Liquid);
    }
  }, [commodityManagementFeature.isAvailable, switchCommodityType, editMode]);

  return (
    <Stack spacing={2}>
      {commodityManagementFeature.isAvailable && !editMode ? (
        <Box alignSelf="flex-end" pr={3}>
          <FormControlLabel
            control={
              <IconSwitch
                offIcon={localGasStationIcon}
                onIcon={truckIcon}
                checked={commodityType === ShipmentCommodityType.Dry}
                inputProps={{
                  "data-testid": testIds.shipmentFormOrderTypeSwitch,
                } as React.InputHTMLAttributes<HTMLInputElement>}
                onChange={(e, checked) => {
                  confirm({
                    title: t("orders:switchOrderType", "Switch order type"),
                    description: t(
                      "orders:switchOrderTypeConfirm",
                      "Switching the order type will clear all the data you have entered. Are you sure you want to proceed?"
                    ),
                    confirmationText: t("common:yes", "Yes"),
                    confirmationButtonProps: {
                      color: "error",
                    },
                    cancellationText: t("common:error.noCancel", "No"),
                  }).then(() => {
                    switchCommodityType(
                      checked
                        ? ShipmentCommodityType.Dry
                        : ShipmentCommodityType.Liquid
                    );
                  });
                }}
              />
            }
            label={t("orders:orderType", "Order Type")}
            labelPlacement="start"
          />
        </Box>
      ) : null}
      {isCommodityDriven ? (
        <CommodityDrivenShipmentForm
          shipmentInput={shipmentInput}
          errors={errors}
          shipmentLocationErrors={shipmentLocationErrors}
          loading={loading}
          validating={validating}
          isChildLoad={isChildLoad}
          isSplit={isSplit}
          onChange={handleChange}
          onUpdate={handleUpdate}
          onValidate={validate}
          onSubmit={handleSubmit}
          isEdit={editMode}
        />
      ) : (
        <LocationDrivenShipmentForm
          shipmentInput={shipmentInput}
          shipmentDetails={toShipment(shipmentInput)}
          errors={errors}
          shipmentLocationErrors={shipmentLocationErrors}
          loading={loading}
          validating={validating}
          isChildLoad={isChildLoad}
          isSplit={isSplit}
          onChange={handleChange}
          onUpdate={handleUpdate}
          onValidate={validate}
          onSubmit={handleSubmit}
          isEdit={editMode}
        />
      )}
    </Stack>
  );
}

const localGasStationIcon = `<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#e8eaed"><path d="M0 0h24v24H0z" fill="none"/><path d="m19.77 7.23.01-.01-3.72-3.72L15 4.56l2.11 2.11c-.94.36-1.61 1.26-1.61 2.33 0 1.38 1.12 2.5 2.5 2.5.36 0 .69-.08 1-.21v7.21c0 .55-.45 1-1 1s-1-.45-1-1V14c0-1.1-.9-2-2-2h-1V5c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v16h10v-7.5h1.5v5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V9c0-.69-.28-1.32-.73-1.77zM12 10H6V5h6v5zm6 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/></svg>`;
const truckIcon = `<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#e8eaed"><path d="M0 0h24v24H0z" fill="none"/><path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>`;
